import { getColumnStartIconRenderer } from "../renderers/statusUtils";
import {
  FILTER_KIND_LABEL_SEPARATOR,
  FILTER_SELECT_COLUMN,
} from "../types/constants";
import { computeDatePresetFromRange } from "../utils/dateUtils";
import {
  createNumberFormatter,
  scaleNumberRange,
  subTypeValueAffixes,
  unscaleNumberRange,
} from "../utils/numberUtils";
import DateFilter, { renderDateFilterChips } from "./DateFilter";
import NumericFilter, { renderNumericFilterChips } from "./NumericFilter";
import OptionFilter, { renderOptionFilterChips } from "./OptionFilter";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import * as labels from "config.labels";
import React, { useMemo } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const ColumnFilterPanel = ({
  open,
  sliderDraggingRef,
  columnName,
  columnDisplayName,
  filterType,
  dataType,
  onBackToMenu,
  searchQuery,
  onSearchQueryChange,
  uniqueValues,
  valueCounts,
  selectedValues,
  onToggleValue,
  onClearSelectedValues,
  getValueDisplayLabel,
  getValueStartIcon,
  minMaxValues,
  numberRange,
  onNumberRangeChange,
  onClearNumberMin,
  onClearNumberMax,
  numberBlankCount = 0,
  onToggleNumberBlank,
  onClearNumberBlank,
  histogramBins,
  dateRange,
  onDateRangeChange,
  dateFormat,
  formatDateInputValue,
  parseDateInputValue,
  onClearDateFrom,
  onClearDateTo,
  dateBlankCount = 0,
  onToggleDateBlank,
  onClearDateBlank,
  numberActions,
  valueActions,
  dateActions,
  valuesEmptyMessage,
  valuesLoading = false,
  valuesUnavailableMessage,
  filterKindLabel,
  valueMultiplier = 1,
  valueSubType = null,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const numFmt = useMemo(
    () => createNumberFormatter(dataType, 1, subTypeValueAffixes(valueSubType)),
    [dataType, valueSubType]
  );
  const shownMinMaxValues = scaleNumberRange(minMaxValues, valueMultiplier);
  const shownNumberRange = scaleNumberRange(numberRange, valueMultiplier);
  const handleNumberRangeChange =
    valueMultiplier === 1
      ? onNumberRangeChange
      : (range) =>
          onNumberRangeChange(unscaleNumberRange(range, valueMultiplier));

  const renderFilterChips = () => {
    switch (filterType) {
      case "date":
        return renderDateFilterChips(
          dateRange,
          dateFormat,
          onClearDateFrom,
          onClearDateTo,
          translateLabels,
          onClearDateBlank,
          () =>
            onDateRangeChange?.({
              from: null,
              to: null,
              isEmpty: dateRange?.isEmpty,
            })
        );
      case "number":
        return renderNumericFilterChips(
          shownNumberRange,
          onClearNumberMin,
          onClearNumberMax,
          numFmt,
          translateLabels,
          onClearNumberBlank
        );
      default:
        return renderOptionFilterChips(
          selectedValues,
          onClearSelectedValues,
          getValueDisplayLabel
        );
    }
  };

  const startIconRenderer = getColumnStartIconRenderer(
    columnName,
    getValueStartIcon
  );
  const renderFilter = () => {
    if (valuesUnavailableMessage) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", px: 2, py: 4 }}>
          <Typography variant={"body2"} color={"text.secondary"}>
            {valuesUnavailableMessage}
          </Typography>
        </Box>
      );
    }
    switch (filterType) {
      case "date":
        return (
          <DateFilter
            pendingRange={dateRange}
            selectedPreset={computeDatePresetFromRange(
              dateRange.from,
              dateRange.to
            )}
            formatDateInputValue={formatDateInputValue}
            parseDateInputValue={parseDateInputValue}
            onDateRangeChange={onDateRangeChange}
            showActions={Boolean(dateActions?.show)}
            onSave={dateActions?.onSave}
            onCancel={dateActions?.onCancel}
            blankCount={dateBlankCount}
            blankSelected={dateRange?.isEmpty === true}
            onToggleBlank={onToggleDateBlank}
          />
        );
      case "number":
        return (
          <NumericFilter
            minMaxValues={shownMinMaxValues}
            pendingRange={shownNumberRange}
            onNumberRangeChange={handleNumberRangeChange}
            histogramBins={histogramBins}
            sliderDraggingRef={sliderDraggingRef}
            showActions={Boolean(numberActions?.show)}
            onSave={numberActions?.onSave}
            onCancel={numberActions?.onCancel}
            dataType={dataType}
            valueSubType={valueSubType}
            blankCount={numberBlankCount}
            blankSelected={numberRange?.isEmpty === true}
            onToggleBlank={onToggleNumberBlank}
            loading={valuesLoading}
          />
        );
      default:
        return (
          <OptionFilter
            columnDisplayName={columnDisplayName}
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            uniqueValues={uniqueValues}
            valueCounts={valueCounts}
            selectedValues={selectedValues}
            onToggleValue={onToggleValue}
            emptyMessage={valuesEmptyMessage}
            loading={valuesLoading}
            isOpen={open}
            getValueDisplayLabel={getValueDisplayLabel}
            getValueStartIcon={startIconRenderer}
            showActions={Boolean(valueActions?.show)}
            onSave={valueActions?.onSave}
            onCancel={valueActions?.onCancel}
          />
        );
    }
  };

  if (!columnName || columnName === FILTER_SELECT_COLUMN) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {onBackToMenu && (
          <IconButton
            size={"small"}
            onClick={onBackToMenu}
            aria-label={translateLabels(labels.SEARCHABLE_LIST_BACK, {
              default: "Back",
            })}
          >
            <ArrowBackIcon fontSize={"small"} />
          </IconButton>
        )}
        <Typography
          variant={"subtitle2"}
          sx={{
            ml: 1,
            flexGrow: 1,
          }}
        >
          {filterKindLabel
            ? `${columnDisplayName}${FILTER_KIND_LABEL_SEPARATOR}${filterKindLabel}`
            : columnDisplayName}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {renderFilterChips()}
      </Box>
      {renderFilter()}
    </Box>
  );
};
export default ColumnFilterPanel;
